import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  UserService,
  CampusWard,
  HousingDataService,
  ApprovedHousingSessionInformation,
  CampusWardsDataService,
  WardGroup,
  WardGroupAssignments,
  SortService,
  ConfirmDialog,
  AcademicSession,
  WardGroupAssignment,
  StudentListing
} from '../../../shared';
import { LeaderComponent } from '../../leader.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { GroupMembers } from './group-members.model';
import { GroupMembersCollection } from './group-members-collection.model';
import { GroupMember } from './group-member.model';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './ward-group-assignments.component.html',
  styleUrls: ['./ward-group-assignments.component.scss']
})
export class WardGroupAssignmentsComponent extends LeaderComponent implements OnInit, OnDestroy {
  loading = false;
  loadingError = false;

  localStudents: StudentListing[];
  housingInformation: ApprovedHousingSessionInformation[];

  groups: WardGroup[];
  selectedGroup: WardGroup;

  assignableGroups: GroupMembersCollection[];
  selectedGroupIndex?: number = null;

  semesterAssignmentsCanBeRolledFrom = null;
  rollingAssignments = false;
  assigningGroup = false;
  assignedGroup: WardGroup;
  removingAssignment = false;
  removedAssignments = false;

  private groupTypeId: number;
  private sessionNumber: number;
  private year: number;
  private session: string;
  private academicSession: AcademicSession;
  private ward: CampusWard;

  private parameterSubscription: Subscription;
  private queryParameterSubscription: Subscription;

  constructor(
    protected userService: UserService,
    protected dialog: MatDialog,
    private route: ActivatedRoute,
    private housingService: HousingDataService,
    private wardService: CampusWardsDataService,
    private sorter: SortService
  ) {
    super(userService, dialog);

    // subscribe to our route parameters' values for the ward or complex and the semester
    this.parameterSubscription = this.route.params.subscribe(params => {
      this.sessionNumber = +params['sessionNumber'];
      this.loadInformation();
    });

    this.queryParameterSubscription = this.route.queryParams.subscribe(params => {
      this.groupTypeId = +params['groupTypeId'];
      this.year = +params['year'];
      this.session = params['session'];
      this.loadInformation();
    });
  }

  ngOnInit() {
    super.ngOnInit();

    // TODO if not proxied, load the ward details that the leader is in charge of
    if (this.isWardLeader()) {
      this.ward = this.getWard();
      this.loadInformation();
    }
  }

  ngOnDestroy() {
    this.parameterSubscription.unsubscribe();
    this.queryParameterSubscription.unsubscribe();
  }

  onWardSelected(ward: CampusWard) {
    if (ward) {
      this.ward = ward;
      this.loadInformation();
    }
  }

  /**
   * Confirms with the user that they want to assign the selected addresses/members to and then does it.
   */
  assignGroup() {
    const members = this.getMembersToAssign();
    if (members.length === 0 || !this.selectedGroup || this.assigningGroup) {
      return;
    }

    const pluralizedPart = this.getSelectedMembersText(members);
    const message = `Are you sure you want to assign ${pluralizedPart} to ${this.selectedGroup.name}?`;
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.clearResultsOfAssignmentOrRemoval();
        this.assigningGroup = true;

        // store this off before in case the user changes their selection while we're updating the group assignments
        const groupToAssign = this.selectedGroup;

        // build our object graph for the updates
        const assignments = new WardGroupAssignments();
        assignments.academicSession = this.academicSession;
        assignments.campusWardGroupTypeId = this.groupTypeId;
        assignments.campusWardId = this.ward.campusWardId;

        members.forEach(member => {
          const assignment = new WardGroupAssignment();
          assignment.campusWardGroupId = groupToAssign.campusWardGroupId;
          assignments.assignments.push(assignment);

          if (member.address) {
            assignment.approvedAddress = member.address.fullAddress;
          } else if (member.student) {
            assignment.localStudent = member.student;
          }
        });

        this.wardService
          .updateWardGroupAssignments(assignments)
          .pipe(
            finalize(() => (this.assigningGroup = false))
          )
          .subscribe(
            updatedAssignments => {
              this.assignedGroup = groupToAssign;
              this.handleAssignments(updatedAssignments);
            },
            error => {
              this.showError('Unable to update group assignments.');
            }
          );
      }
    });
  }

  /**
   * Confirms with the user that they want to remove the assigned group of the selected addresses and/or local students, and if they confirm,
   * does it.
   */
  removeAssignments() {
    const members = this.getMembersToAssign();
    if (members.length === 0 || this.removingAssignment) {
      return;
    }

    const pluralizedPart = this.getSelectedMembersText(members);
    const message = `Are you sure you want to remove the assigned group of ${pluralizedPart}?`;
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        const ids = members.filter(m => m.id).map(m => m.id);
        this.clearResultsOfAssignmentOrRemoval();
        this.removingAssignment = true;

        this.wardService
          .removeWardGroupAssignments(this.ward.campusWardId, ids)
          .pipe(
            finalize(() => (this.removingAssignment = false))
          )
          .subscribe(
            () => {
              this.removedAssignments = true;
              this.loadAssignments();
            },
            error => {
              this.showError('Unable to remove the group assignment(s).');
            }
          );
      }
    });
  }

  /**
   * Confirms the user wants to, then rolls the assignments from the previous semester to this one.
   */
  rollAssignments() {
    if (this.rollingAssignments || !this.semesterAssignmentsCanBeRolledFrom) {
      return;
    }

    const message = `Are you sure you want to roll the group assignments from ${this.semesterAssignmentsCanBeRolledFrom.description}?`
    this.openConfirmDialog(message).afterClosed().subscribe(response => {
      if (response) {
        this.rollingAssignments = true;
        this.wardService
          .rollWardGroupAssignments(this.ward.campusWardId, this.groupTypeId, this.year, this.session)
          .pipe(
            finalize(() => (this.rollingAssignments = false))
          )
          .subscribe(
            (rolledAssignments) => {
              this.handleAssignments(rolledAssignments);
            },
            error => {
              this.showError('Unable to roll the group assignment(s).');
            }
          );
      }
    });
  }

  /**
   * Clears things that are set after an assignment is made or assignments are removed.
   */
  private clearResultsOfAssignmentOrRemoval() {
    this.assignedGroup = null;
    this.removedAssignments = false;
  }

  /**
   * Gets the text to use when telling the user what they're doing will be done to.  For example,
   * 'this address' or 'this student and these 10 addresses'.
   */
  private getSelectedMembersText(members: GroupMember[]): string {
    const selectedAddresses = members.filter(m => m.address !== null);
    const selectedStudents = members.filter(m => m.student !== null);

    if (members.length === 1) {
      const type = selectedAddresses.length > 0 ? 'address' : 'student';
      return `this ${type}`;
    }

    let addressMessage = '';
    let addressPronoun = '';
    if (selectedAddresses.length === 1) {
      addressMessage = 'address';
      addressPronoun = 'this ';
    } else if (selectedAddresses.length > 1) {
      addressMessage = `${selectedAddresses.length} addresses`;
      addressPronoun = 'these ';
    }

    let studentMessage = '';
    let studentPronoun = '';
    if (selectedStudents.length === 1) {
      studentMessage = 'student';
      studentPronoun = 'this ';
    } else if (selectedStudents.length > 1) {
      studentMessage = `${selectedStudents.length} students`;
      studentPronoun = 'these ';
    }

    const hasBothTypes = selectedAddresses.length > 0 && selectedStudents.length > 0;
    return `${addressPronoun}${addressMessage}${hasBothTypes ? ' and ' : ''}${studentPronoun}${studentMessage}`;
  }

  /**
   * Opens a confirm dialog with the given message.
   */
  private openConfirmDialog(message: string): MatDialogRef<ConfirmDialog, any> {
    return this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: 'Please confirm', message: message }
    });
  }

  /**
   * Gets the addresses or local students that the user has selected to clear an assignment or make a new assignment for.
   */
  private getMembersToAssign(): GroupMember[] {
    if (this.selectedGroupIndex != null && this.selectedGroupIndex >= 0) {
      return this.assignableGroups[this.selectedGroupIndex].selectedMembers;
    }

    return [];
  }

  /**
   * Loads the information that we need to work with assignments.
   */
  private loadInformation() {
    this.selectedGroupIndex = null;
    this.clearResultsOfAssignmentOrRemoval();

    if (this.ward && !isNaN(this.sessionNumber) && this.session && !isNaN(this.year) && !isNaN(this.groupTypeId)) {
      // load the addresses that are applicable to this ward for the given semester and group type
      this.loading = true;
      forkJoin(
        this.housingService.getHousingComplexSessionInformation(this.ward.campusWardId, this.sessionNumber, this.groupTypeId),
        this.wardService.getWardGroupsOfType(this.ward.campusWardId, this.groupTypeId),
        this.wardService.getLocalStudents(this.ward.campusWardId, this.groupTypeId),
        this.wardService.getWardGroupAssignments(this.ward.campusWardId, this.groupTypeId, this.year, this.session)
      )
      .pipe(
        finalize(() => (this.loading = false))
      )
      .subscribe(
        ([housingInformation, groups, localStudents, assignments]) => {
          this.handleInformation(housingInformation, groups, localStudents, assignments);
        },
        error => {
          this.loadingError = true;
        }
      );
    }
  }

  /**
   * Reloads just the group assignments.
   */
  private loadAssignments() {
    this.loading = true;
    this.wardService
      .getWardGroupAssignments(this.ward.campusWardId, this.groupTypeId, this.year, this.session)
      .pipe(
        finalize(() => (this.loading = false))
      )
      .subscribe(assignments => this.handleAssignments(assignments), error => (this.loadingError = true));
  }

  /**
   * Sets up our groups of assignments so that the user can view and make changes.
   *
   * @param housingInformation All of the complexes/addresses available for our group type
   * @param groups All of the ward's groups of our group type
   * @param assignments All of the current assignments of the ward to these groups
   */
  private handleInformation(housingInformation: ApprovedHousingSessionInformation[], groups: WardGroup[], localStudents: StudentListing[], assignments: WardGroupAssignments) {
    this.housingInformation = housingInformation;
    this.localStudents = localStudents;
    this.groups = groups;
    this.groups.sort(this.sorter.sortWardGroupsAsc);

    this.handleAssignments(assignments);
  }

  /**
   * Sets up our groups of assignments so that the user can view and make changes.
   *
   * @param housingInformation All of the complexes/addresses available for our group type
   * @param groups All of the ward's groups of our group type
   * @param groupAssignments All of the current assignments of the ward to these groups
   */
  private handleAssignments(groupAssignments: WardGroupAssignments) {
    // before we rebuild our tabs, see if we already have one selected and if so, we'll try to reselect it
    let groupIdToSelect = null;
    if (this.selectedGroupIndex != null) {
      groupIdToSelect = this.assignableGroups[this.selectedGroupIndex].id;
    }

    this.assignableGroups = [];
    this.academicSession = groupAssignments.academicSession;
    this.semesterAssignmentsCanBeRolledFrom = groupAssignments.semesterAssignmentsCanBeRolledFrom;

    // create a collection for our ones that are unassigned
    const unassignedGroup = new GroupMembersCollection('Unassigned');

    // create a collection for each of our groups
    this.groups.forEach(group => {
      this.assignableGroups.push(new GroupMembersCollection(group.name, group.campusWardGroupId));
    });

    // go through all of our addresses, and if there's an assignment, put it in that group; otherwise put it in the unassigned group
    this.housingInformation.forEach(complex => {
      // go through each address and see if it's already been assigned
      complex.assignedApprovedAddresses.forEach(address => {
        // the collection we'll add the address to; either the assigned Group or "Unassigned"
        let groupCollection: GroupMembersCollection = null;

        // see if it's assigned already, and if so put it in that group; otherwise the unassigned one
        const assignment = groupAssignments.assignments.find(a => a.approvedAddress === address.fullAddress);
        if (assignment !== undefined) {
          groupCollection = this.assignableGroups.find(g => g.id === assignment.campusWardGroupId);
          // there could be an assignment in a group they inactivated, so if so, move it to unassigned
          if (groupCollection === undefined) {
            groupCollection = unassignedGroup;
          }
        } else {
          groupCollection = unassignedGroup;
        }

        // now group it by the complex; create one if we need to
        let complexMembers = groupCollection.members.find(m => m.title === address.complex.name);
        if (complexMembers === undefined) {
          complexMembers = new GroupMembers(address.complex.name);
          groupCollection.members.push(complexMembers);
        }
        complexMembers.members.push(new GroupMember(address, null, assignment ? assignment.campusWardGroupAssignmentId : null));
      });
    });

    // go through all of our local students, and if there's an assignment, pit it in that group; otherwise put it in the unassigned group
    this.localStudents.forEach(student => {
       // the collection we'll add the student to; either the assigned Group or "Unassigned"
       let groupCollection: GroupMembersCollection = null;

       // see if they're assigned already, and if so put them in that group; otherwise the unassigned one
       const assignment = groupAssignments.assignments.find(a => a.localStudent && a.localStudent.iNumber === student.iNumber);
       if (assignment !== undefined) {
         groupCollection = this.assignableGroups.find(g => g.id === assignment.campusWardGroupId);
         // there could be an assignment in a group they inactivated, so if so, move it to unassigned
         if (groupCollection === undefined) {
           groupCollection = unassignedGroup;
         }
       } else {
         groupCollection = unassignedGroup;
       }

       // now group it by the complex; create one if we need to
       let studentMembers = groupCollection.members.find(m => m.title === 'Local Students');
       if (studentMembers === undefined) {
         studentMembers = new GroupMembers('Local Students');
         groupCollection.members.push(studentMembers);
       }
       studentMembers.members.push(new GroupMember(null, student, assignment ? assignment.campusWardGroupAssignmentId : null));
    });

    // add in the unassigned group FIRST if there are any there
    if (unassignedGroup.members.length > 0) {
      this.assignableGroups.splice(0, 0, unassignedGroup);
    }

    // sort the members within each collection
    this.assignableGroups.forEach(g => {
      g.members.sort(sortGroupMembersAsc);
      g.members.forEach(m => m.members.sort(sortMembersAsc));
    });

    if (this.assignableGroups.length > 0) {
      if (groupIdToSelect != null) {
        this.selectedGroupIndex = this.assignableGroups.map(g => g.id).indexOf(groupIdToSelect);
      } else {
        this.selectedGroupIndex = 0;
      }
    }
  }
}

/**
 * Sorts two GroupMembers based on title.
 */
function sortGroupMembersAsc(one: GroupMembers, two: GroupMembers): number {
  return one.title.toUpperCase().localeCompare(two.title.toUpperCase());
}

/**
 * Sorts two GroupMembers based on address or local student name.
 */
function sortMembersAsc(one: GroupMember, two: GroupMember): number {
  const address1 = one.address;
  const address2 = two.address;
  const student1 = one.student;
  const student2 = two.student;

  if (address1 && address2) {
    let compared = address1.addressWithoutApartment.toUpperCase().localeCompare(address2.addressWithoutApartment);
    if (compared === 0) {
      if (address1.apartmentNumber && address2.apartmentNumber) {
        compared = address1.apartmentNumber - address2.apartmentNumber;
      } else if (address1.apartmentLetter && address2.apartmentLetter) {
        compared = address1.apartmentLetter.toUpperCase().localeCompare(address2.apartmentLetter.toUpperCase());
      }
    }
    return compared;
  } else if (student1 && student2) {
    let compared = student1.lastName.toUpperCase().localeCompare(student2.lastName.toUpperCase());
    if (compared === 0) {
      compared = student1.firstName.toUpperCase().localeCompare(student2.firstName.toUpperCase());
    }
    return compared;
  }

  return one.title.toUpperCase().localeCompare(two.title.toUpperCase());
}
